import { SerializedVehicleReport } from '@/reports/types/serialized';
import {
  AutomobileTitleRegistration,
  AutomobileTitleRegistrations,
  AutomobileTitleRegistrationCore,
} from '@/reports/types/vehicles';
import { Icons } from '@/theme/index';
import { pluralizeString } from '@/utils/pluralizeString';
import { ConditionalBadge } from './ConditionalBadge/ConditionalBadge';
import { VehicleReportInfoCard } from './VehicleReportInfoCard/VehicleReportInfoCard';
import { StyledStack } from './components';

interface VehicleReportInfoCarouselProps {
  vehicleReportData: SerializedVehicleReport;
  handleFreeUpgrade: () => void;
}

export const VehicleReportInfoCarousel = ({
  vehicleReportData,
  handleFreeUpgrade,
}: VehicleReportInfoCarouselProps) => {
  const currentVehicle = vehicleReportData.automobiles[0];
  const accidentsCount =
    (currentVehicle?.accidentsCount || 0) +
    (currentVehicle?.salvagesCount || 0);
  const ownershipHistoryLength = currentVehicle?.ownership_history.length || 0;
  const titleRecords =
    (currentVehicle?.title_registrations as AutomobileTitleRegistrations) || [];
  // Put all the years from the title records an array
  const titleYears: number[] = titleRecords
    .map((title) => [
      title.date?.parsed?.year,
      ...title.history.map(
        (item: AutomobileTitleRegistrationCore) => item.date?.parsed?.year
      ),
    ])
    .flat()
    .filter((year): year is number => year !== null && year !== undefined);
  // Add the year of the first ownership history record
  if (currentVehicle?.ownership_history?.[0]?.data_date?.parsed?.year) {
    titleYears.push(currentVehicle.ownership_history[0].data_date.parsed.year);
  }
  // Get the year of the vehicle's first owner.
  const initialOwnershipYear =
    Boolean(titleYears.length) && Math.min(...titleYears);
  const currentYear = new Date().getFullYear();
  const yearsOfOwnership =
    currentYear && initialOwnershipYear
      ? currentYear - initialOwnershipYear
      : 0;
  const isUpgradedAndSuccess =
    vehicleReportData.report_upgraded &&
    vehicleReportData.automobiles[0]?.nmvtis_success;
  // Count the number of owners for each mileage change
  const odometerValues: number[] = [];
  const ownersByTitleRecords = titleRecords.reduce(
    (total: number, title: AutomobileTitleRegistration) => {
      const itemsWithMileage = title.history.reduce((count, item) => {
        if (item.odometer != null && !odometerValues.includes(item.odometer)) {
          odometerValues.push(item.odometer);
          return count + 1;
        }
        return count;
      }, 0);

      const titleWithMileageCount =
        title.odometer != null && !odometerValues.includes(title.odometer)
          ? 1
          : 0;

      return total + itemsWithMileage + titleWithMileageCount;
    },
    0
  );

  const owners = isUpgradedAndSuccess
    ? Math.max(ownersByTitleRecords, ownershipHistoryLength)
    : ownershipHistoryLength;
  const salvageCount =
    (currentVehicle?.salvagesCount || 0) +
    (currentVehicle?.titleChecksCount || 0);
  const openRecallsLength = currentVehicle?.openRecallsCount || 0;
  const damageReportedCount =
    currentVehicle?.damage_history?.reported_damage.length ||
    0 ||
    currentVehicle?.auctionsDamage.length ||
    0;
  const damageCount = accidentsCount + damageReportedCount;
  const damagePrefix =
    damageReportedCount && !accidentsCount
      ? `${damageReportedCount} ${pluralizeString(
          'Damage',
          damageReportedCount
        )}`
      : `${damageCount || 'No'} ${pluralizeString('Accident', accidentsCount)}`;
  const damageSuffix =
    !damageReportedCount && !accidentsCount
      ? 'or Damage'
      : damageReportedCount && accidentsCount
      ? 'and Damage'
      : '';

  const vehicleReportInfoCards = [
    {
      icon: (
        <ConditionalBadge
          recordedItem={damageCount > 0}
          icon={Icons.MinorCrash}
        />
      ),
      text: `${damagePrefix} ${damageSuffix} Reported`.trim(),
      link: 'vehicle-damage-history',
    },
    {
      icon: (
        <ConditionalBadge
          showBadge={owners > 0}
          recordedItem={owners > 1}
          customBadgeIconColor={owners > 1 && owners <= 3 ? '#ED6C02' : ''}
          customBoxBackgroundColor={owners > 1 && owners <= 3 ? '#FDF4EB' : ''}
          icon={owners > 1 ? Icons.PeopleOutlineRounded : Icons.PersonRounded}
        />
      ),
      text: owners ? `${owners} ${pluralizeString('Owner', owners)}` : 'Owners',
      subText:
        owners && yearsOfOwnership
          ? `In ${yearsOfOwnership} ${pluralizeString(
              'Year',
              yearsOfOwnership
            )}`
          : owners && !yearsOfOwnership
          ? ''
          : 'No Records Found',
      link:
        titleRecords.length > 0 && ownershipHistoryLength === 0
          ? 'vehicle-title-records'
          : 'vehicle-ownership-history',
    },
    {
      icon: (
        <ConditionalBadge
          recordedItem={openRecallsLength > 0}
          icon={Icons.BuildRounded}
        />
      ),
      text: `${
        openRecallsLength > 0 ? openRecallsLength : 'No'
      } Open ${pluralizeString('Recall', openRecallsLength)}`,
      link: 'vehicle-recalls',
    },
    {
      icon: (
        <ConditionalBadge
          showBadge={isUpgradedAndSuccess}
          recordedItem={salvageCount > 0}
          icon={Icons.HistoryEduOutlined}
        />
      ),
      text: !isUpgradedAndSuccess
        ? 'Unlock Titles'
        : salvageCount > 0
        ? 'Branded Title'
        : 'Clean Title',
      link:
        currentVehicle?.salvagesCount > 0 &&
        currentVehicle?.titleChecksCount === 0
          ? 'vehicle-damage-history'
          : 'vehicle-title-brands',
    },
  ];

  return (
    <StyledStack
      flexWrap={'wrap'}
      justifyContent={'flex-start'}
      data-testid="vehicle-report-info-overview-cards"
    >
      {vehicleReportInfoCards.map((card, index) => (
        <VehicleReportInfoCard
          index={index}
          card={card}
          showButtonView={
            (!isUpgradedAndSuccess && card.link === 'vehicle-title-brands') ||
            (!isUpgradedAndSuccess &&
              card.link === 'vehicle-salvages-report-section')
          }
          handleFreeUpgrade={handleFreeUpgrade}
        />
      ))}
    </StyledStack>
  );
};
